/**
 * Background MUSIC — a shuffle of tracker-module tracks (public/music/), streamed ONE at a time
 * through a single reused HTMLAudioElement (lazy HTTP streaming; never decoded into memory like the
 * WebAudio sfx engine, so 23 tracks cost nothing until they play). Ported from wiiplaytanks (the
 * user's own game). Its own gentle volume (localStorage "lfmusicvol", capped by MUSIC_CEIL so it
 * stays under the battle sfx), honors the global mute (M), and fires onNowPlaying so the HUD can
 * show a credit toast. Begins once a user gesture unlocks audio; reshuffles at the end of each cycle.
 */

export interface MusicTrack {
  file: string; // "/music/NN.mp3" (served from public/)
  title: string; // "Artist - Track"
  format: string; // short module format: MOD / XM / IT / MPTM
  channels: number; // tracker channel count
  url: string; // YouTube video — the credit link
  duration: number;
  formatLong?: string;
}

const MUSIC_CEIL = 0.5; // even at slider 100% the music stays under the game sounds

class Music {
  private audio = new Audio();
  private tracks: MusicTrack[] = [];
  private order: number[] = []; // shuffled play order
  private pos = -1;
  private vol: number; // user setting 0..1 (the slider)
  private muted = false;
  private loaded = false;
  private unlocked = false;
  private wantPlay = false; // start() called before the unlock gesture → begin on unlock
  private started = false;
  private gestureInstalled = false;
  /** Fired when a new track begins — the HUD shows the credit toast. */
  onNowPlaying: (t: MusicTrack) => void = () => {};

  constructor() {
    const saved = parseFloat(localStorage.getItem("lfmusicvol") ?? "");
    this.vol = isNaN(saved) ? 0.6 : Math.max(0, Math.min(1, saved));
    if (localStorage.getItem("lfsfxmute") === "1") this.muted = true; // share the sfx mute default
    this.audio.preload = "none";
    this.audio.addEventListener("ended", () => this.advance());
    this.audio.addEventListener("error", () => { if (this.started) this.advance(); }); // a bad track skips on
    this.installGestureUnlock();
    this.apply();
  }

  private apply(): void {
    this.audio.volume = this.muted ? 0 : Math.max(0, Math.min(1, this.vol * MUSIC_CEIL));
  }

  /** Unlock on the first user gesture (the same one the sfx engine resumes on). */
  private installGestureUnlock(): void {
    if (this.gestureInstalled) return;
    this.gestureInstalled = true;
    const unlock = (): void => {
      this.unlock();
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("click", unlock);
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    window.addEventListener("click", unlock);
  }

  private async loadManifest(): Promise<void> {
    if (this.loaded) return;
    this.loaded = true;
    try {
      const m = (await fetch("/music/manifest.json").then((r) => r.json())) as MusicTrack[];
      this.tracks = Array.isArray(m) ? m : [];
      this.reshuffle();
    } catch {
      this.loaded = false; // allow a retry on the next start()
    }
  }

  private reshuffle(): void {
    this.order = this.tracks.map((_, i) => i);
    for (let i = this.order.length - 1; i > 0; i--) {
      const j = (Math.random() * (i + 1)) | 0;
      [this.order[i], this.order[j]] = [this.order[j], this.order[i]];
    }
  }

  unlock(): void {
    this.unlocked = true;
    if (this.wantPlay) { this.wantPlay = false; void this.begin(); }
  }

  /** Begin ambient playback. Idempotent; defers until the unlock gesture. */
  start(): void {
    if (this.started) return;
    if (!this.unlocked) { this.wantPlay = true; return; }
    void this.begin();
  }

  private async begin(): Promise<void> {
    if (this.started) return;
    this.started = true; // set synchronously to block re-entry across the await
    await this.loadManifest();
    if (!this.tracks.length) { this.started = false; return; }
    this.advance();
  }

  private advance(): void {
    if (!this.tracks.length) return;
    this.pos++;
    if (this.pos >= this.order.length) { this.pos = 0; this.reshuffle(); } // fresh shuffle each cycle
    const t = this.tracks[this.order[this.pos]];
    this.audio.src = t.file;
    this.apply();
    if (!this.muted) void this.audio.play().catch(() => {});
    this.onNowPlaying(t);
  }

  /** Skip to the next track. */
  next(): void { if (this.started) this.advance(); }

  setVolume(v: number): void {
    this.vol = Math.max(0, Math.min(1, v));
    this.apply();
    try { localStorage.setItem("lfmusicvol", String(this.vol)); } catch { /* ignore */ }
  }
  getVolume(): number { return this.vol; }

  /** Follow the global mute (the M key). Pauses the stream when muted, resumes on unmute. */
  setMuted(m: boolean): void {
    this.muted = m;
    this.apply();
    if (m) this.audio.pause();
    else if (this.started) void this.audio.play().catch(() => {});
  }

  currentTrack(): MusicTrack | undefined {
    return this.started && this.pos >= 0 ? this.tracks[this.order[this.pos]] : undefined;
  }
}

export const music = new Music();
